import React from 'react'

import { currencySymbol } from '../lib/currency-formatter'

import NumberInput from './NumberInput'
import { I18n, Translate } from 'react-redux-i18n'
import { BondTradingPane } from '../types'
import Spinner from './Spinner'

import { ClearSelectBond } from '../actions/bondActions'
import { notifyParentSignupRequired } from '../utils/common-utils'
import { Collapse } from 'reactstrap'
import {
  calculateEffectiveYield,
  calculatePrincipalAmountFromEnteredAmount,
  calculateRequiredFunds,
  TradingAccount,
  calcBondAmountValue,
  BondBuyMode,
  BondConfigItem,
} from '@bdswiss/mt4-connector'
import BondDetailDialog from '../containers/BondDetailDialog'

export interface Props {
  accountLoaded: boolean
  assetLoaded: boolean
  account?: TradingAccount
  locale: string
  loadingImage: string
  paneBondsValues: BondTradingPane
  onSetBondAmount: (amount: number) => void
  buyBond: (symbol: string, volume: number) => void
  onChangeBondByMode: (buyMode: BondBuyMode) => void
  showBondDetailDialog: () => void
  selectedBond?: BondConfigItem
  mode: string
  demo?: string
  clearSelectBond: () => ClearSelectBond
}
export interface State {
  advancedOpen: boolean
  amount: number
}

const BondsPane = (props: Props) => {
  return <BondsPaneContent {...props} />
}

class BondsPaneContent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.toggleAdvanced = this.toggleAdvanced.bind(this)
    this.onChangeAmount = this.onChangeAmount.bind(this)
    this.state = {
      advancedOpen: false,
      amount: this.props.paneBondsValues.amount,
    }
  }

  public render() {
    if (!this.props.account) {
      console.log('Bonds Trading pane loading...')
      return <Spinner />
    }

    const {
      account,
      accountLoaded,
      assetLoaded,
      buyBond,
      onSetBondAmount,
      selectedBond,
      demo,
      mode,
    } = this.props
    const {
      amountInitial,
      rate,
      amount,
      tradeOperationEnabled,
      bondBuyMode,
    } = this.props.paneBondsValues
    const calcAmount = this.state.amount
    const accountCurrency = account.currency
    const bondCurrency = selectedBond !== undefined ? selectedBond.currency : ''
    const pricePer100 = selectedBond !== undefined ? selectedBond.ask : 0
    // const leverage = (selectedBond !== undefined) ? selectedBond.leverage : 1;
    const showPriceInAccountCurrency = accountCurrency !== bondCurrency

    const accruedInterestForBond = selectedBond !== undefined ? selectedBond.accruedInterest : 0
    let principalValue = 0
    let accruedInterest = 0
    let effectiveYield = 0
    let projectedInterestEarnings = 0
    let totalCost = 0
    let requiredFunds = 0

    if (selectedBond !== undefined) {
      principalValue = calculatePrincipalAmountFromEnteredAmount(
        selectedBond,
        calcAmount,
        rate,
        bondBuyMode
      )

      accruedInterest = (principalValue * accruedInterestForBond) / selectedBond.faceValue
      totalCost = (principalValue * pricePer100) / 100 + accruedInterest
      requiredFunds = calculateRequiredFunds(selectedBond, principalValue)
    }

    // per Sergey, order amount should be integer value, so flooring
    const amountForOrder = Math.floor(principalValue)
    const totalCostAccountCurrency = totalCost * rate

    if (selectedBond !== undefined) {
      effectiveYield = calculateEffectiveYield(selectedBond, calcAmount)
      projectedInterestEarnings = (effectiveYield * requiredFunds) / 100
    }
    const needShowRightPane = selectedBond && !(demo === 'bonds' && mode === 'embedded')

    const classT = `sidebar ${needShowRightPane ? 'show' : ''}`

    const digits = 2

    if (!accountLoaded || !assetLoaded) {
      return (
        <div className="sidebar show">
          <Spinner />
        </div>
      )
    }

    let buyButton
    if (selectedBond) {
      const { mt4Symbol } = selectedBond
      buyButton = (
        <button
          onClick={() => (demo ? notifyParentSignupRequired() : buyBond(mt4Symbol, amountForOrder))}
          disabled={!tradeOperationEnabled}
          className="btn black_button col"
          data-cy="placeTrade"
        >
          <Translate value={'placeOrder'} />
        </button>
      )
    } else {
      buyButton = <div />
    }

    const amountLabel =
      bondBuyMode === BondBuyMode.BOND_BUY_MODE_CASH ? (
        <span>
          <Translate value={'cashAmount'} />, {currencySymbol(bondCurrency)}
        </span>
      ) : (
        <Translate value={'principalAmount'} />
      )
    const starChar =
      bondBuyMode === BondBuyMode.BOND_BUY_MODE_CASH ? (
        <sup className="bondBuyModeDisclamer">*</sup>
      ) : (
        ''
      )
    const bondBuyModeDisclamer =
      bondBuyMode === BondBuyMode.BOND_BUY_MODE_CASH ? (
        <span>{I18n.t('bondBuyModeDisclamer')}</span>
      ) : (
        ''
      )

    return (
      <div className={classT} data-cy="bondPane">
        <div className="hidden-wrapper__header d-flex justify-content-between align-items-center no-padding">
          <h2 className="sidebar__title">{selectedBond ? selectedBond.name : selectedBond}</h2>
          <button
            onClick={() => this.props.clearSelectBond()}
            className="btn close_btn"
            data-cy="bondPaneClose"
          >
            <i className="icon-close" />
          </button>
        </div>
        <div className="d-flex align-items-center mb-3">
          <div className="w-50 mr-3">
            <img src={selectedBond ? selectedBond.logoUrl : ''} alt="" className="img-responsive" />
          </div>
        </div>
        <div className="d-flex flex-wrap">
          <div className="my-2 w-100">
            <div className="sidebar__label">
              <Translate value={'selectInvestmentType'} />
            </div>
            <div className="font-weight-medium fz-20">
              <select
                className="bond-buy-mode-select"
                value={bondBuyMode}
                onChange={e => this.props.onChangeBondByMode(e.target.value as BondBuyMode)}
                data-cy="bondBuyMode"
              >
                <option value={BondBuyMode.BOND_BUY_MODE_CASH} data-cy="cash">
                  {I18n.t('cash')}
                </option>
                <option value={BondBuyMode.BOND_BUY_MODE_PRINCIPAL} data-cy="principal">
                  {I18n.t('principal')}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div className="d-flex flex-row justify-content-between my-2 pb-1">
          <div>
            <span className="sidebar__label">{amountLabel}</span>
          </div>
          <div
            className="font-weight-medium view-bond-detail-link"
            onClick={() => {
              this.props.showBondDetailDialog()
            }}
          >
            <ins>
              <Translate value={'viewDetails'} />
            </ins>
          </div>
        </div>
        <div className="counter-group">
          <NumberInput
            valueInitial={amountInitial}
            placeholder={I18n.t('amount')}
            value={amount}
            precision={0}
            step={1}
            setValue={val => onSetBondAmount(+val)}
            calcValue={calcBondAmountValue.bind(null)}
            valueChanged={this.onChangeAmount}
            onInputValid={() => true}
          />
        </div>
        <div className="d-flex flex-wrap">
          <div className="my-2 w-75">
            <div className="sidebar__label">
              <Translate value={'requiredFunds'} />:
            </div>
            <div className="sidebar__label_description">
              (<Translate value={'marginRequirement'} />)
            </div>
          </div>
          <div className="my-2 w-25 bdswiss-table--right">
            <div className="font-weight-medium">
              {currencySymbol(bondCurrency)}
              {requiredFunds.toFixed(digits)}
              {starChar}
            </div>
          </div>

          <div className="my-2 w-75">
            <div className="sidebar__label">
              <Translate value={'yearlyLeveragedInterest'} />:
            </div>
          </div>
          <div className="my-2 w-25 bdswiss-table--right">
            <div className="font-weight-medium">{effectiveYield.toFixed(2)}%</div>
          </div>
          <div className="my-2 w-75">
            <div className="sidebar__label">
              <Translate value={'yearlyInterestEarnings'} />:
            </div>
          </div>
          <div className="my-2 w-25 bdswiss-table--right">
            <div className="font-weight-medium">
              {currencySymbol(bondCurrency)}
              {projectedInterestEarnings.toFixed(digits)}
            </div>
          </div>
        </div>
        <div
          onClick={this.toggleAdvanced}
          className="trade-settings-advanced--label d-flex flex-row align-items-center color-33"
          data-cy="advancedSettings"
        >
          <div className={'wt-expander wte' + (this.state.advancedOpen ? '-down' : '-right')}>
            &nbsp;
          </div>
          {I18n.t('advanced')}
        </div>
        <Collapse isOpen={this.state.advancedOpen} className="collapseAdvanced">
          <div className="d-flex flex-wrap">
            <div className="my-2 w-50">
              <div className="sidebar__label">
                {I18n.t('pricePer', { currency: currencySymbol(bondCurrency) })}
              </div>
              <div className="font-weight-medium">
                {currencySymbol(bondCurrency)}
                {pricePer100.toFixed(digits)}
              </div>
            </div>
            <div className="my-2 w-50">
              <div className="sidebar__label">
                <Translate value={'marketValue'} />
              </div>
              <div className="font-weight-medium">
                {currencySymbol(bondCurrency)}
                {principalValue.toFixed(digits)}
              </div>
            </div>
            <div className="my-2 w-50">
              <div className="sidebar__label">
                <Translate value={'accruedInterest'} />
              </div>
              <div className="font-weight-medium">
                {currencySymbol(bondCurrency)}
                {accruedInterest.toFixed(digits)}
              </div>
            </div>
            <div className="my-2 w-50">
              <div className="sidebar__label">
                <Translate value={'totalCost'} />
              </div>
              <div className="font-weight-medium">
                {currencySymbol(bondCurrency)}
                {totalCost.toFixed(digits)}
                {showPriceInAccountCurrency
                  ? ' = ' +
                    currencySymbol(accountCurrency) +
                    totalCostAccountCurrency.toFixed(digits)
                  : ''}
                {starChar}
              </div>
            </div>
          </div>
        </Collapse>

        <div className="align-items-center d-flex mt-3 mb-4">{buyButton}</div>
        <div className="bondBuyModeDisclamer">{bondBuyModeDisclamer}</div>
        <BondDetailDialog />
      </div>
    )
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.paneBondsValues.amount !== prevProps.paneBondsValues.amount) {
      this.setState(pState => ({
        amount: this.props.paneBondsValues.amount,
      }))
    }
  }

  private onChangeAmount(amount: number) {
    this.setState(prevState => ({
      amount,
    }))
  }

  private toggleAdvanced() {
    this.setState(prevState => ({
      advancedOpen: !prevState.advancedOpen,
    }))
  }
}

export default BondsPane
